import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Maximize2 } from "lucide-react";
import { ClientRail } from "../../pages/comms/Messages/ClientRail";
import { UserProfileStrip } from "../../pages/Dashboard/UserListDashboard/UserProfileStrip";
import { EmbeddedFlowHostProvider } from "../embeddedFlow/EmbeddedFlowContext";
import { EmbeddedFlow } from "../embeddedFlow/EmbeddedFlow";
import { findFlowFor } from "../embeddedFlow/embeddableFlows";
import { serializeUserProfileKey } from "../../utils/listCache/caches";

/**
 * A client's profile, shown wherever `openClientProfile` slides the drawer in
 * — Support tickets and chat threads.
 *
 * This is deliberately a pinned `UserProfileStrip` over `ClientRail` in
 * `dashboard` mode, which mirrors `/dashboard/:id` tab for tab. Anything less
 * — the bare `ProfileCard` this used to be, or a hand-picked subset of tabs —
 * is a second, subtly different version of a screen people already know, and
 * it sends them back to the Users list for whatever it left out.
 *
 * There is no Chat tab here on purpose: this panel can open over a support
 * ticket, and support agents must not see coaching room content. The same
 * rule is why `features/chatDock` keeps its thread body out of
 * `common/comms`, where this panel could reach it.
 *
 * @param {Object} props
 * @param {string} props.clientId - FlexBoard user id.
 * @param {string} [props.clientName] - Name from the row that was clicked,
 *   used until the profile payload resolves.
 */
export function ClientProfilePanel({ clientId, clientName }) {
  const navigate = useNavigate();
  // The plan editor currently open inline, as { flow, to, state }.
  const [activeFlow, setActiveFlow] = React.useState(null);

  const handleExpand = useCallback(() => {
    navigate(`/dashboard/${clientId}`);
  }, [navigate, clientId]);

  const canEmbedFlow = useCallback((to) => Boolean(findFlowFor(to)), []);

  const openFlow = useCallback((to, options) => {
    const flow = findFlowFor(to);
    if (flow) setActiveFlow({ flow, to, state: options?.state });
  }, []);

  const closeFlow = useCallback(() => setActiveFlow(null), []);

  // Switching clients must not leave a previous one's editor open.
  const flowClientRef = React.useRef(clientId);
  if (flowClientRef.current !== clientId) {
    flowClientRef.current = clientId;
    if (activeFlow) setActiveFlow(null);
  }

  if (!clientId) return null;

  const actions = (
    <button
      type="button"
      onClick={handleExpand}
      aria-label="Open as full page"
      title="Open as full page"
      className="rounded-lg border border-gray-200 p-1.5 text-gray-600 hover:bg-gray-50"
    >
      <Maximize2 className="h-4 w-4" />
    </button>
  );

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col">
      {/* Pinned: the profile facts stay on screen whichever tab is open. */}
      <UserProfileStrip
        key={clientId}
        userId={clientId}
        fallbackName={clientName}
        actions={actions}
      />

      {/* `showProfileCard={false}` — the strip above replaces the rail's own
          400px ProfileCard, and both read the same cached payload so the
          profile isn't fetched twice. */}
      <div className="min-h-0 flex-1 overflow-hidden">
        {activeFlow ? (
          <EmbeddedFlow
            flow={activeFlow.flow}
            to={activeFlow.to}
            state={activeFlow.state}
            onExit={closeFlow}
            backLabel={clientName ? `Back to ${clientName}` : "Back"}
          />
        ) : (
          <EmbeddedFlowHostProvider canEmbed={canEmbedFlow} onOpen={openFlow}>
            <ClientRail
              key={clientId}
              clientId={clientId}
              mode="dashboard"
              layout="pane"
              showProfileCard={false}
              cacheKey={serializeUserProfileKey(clientId)}
            />
          </EmbeddedFlowHostProvider>
        )}
      </div>
    </div>
  );
}

export default ClientProfilePanel;
